// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import { FinertyLogo } from '@loa/ui-components';

interface LogoWithBrandingProps {
  /** URL of the brokerage logo */
  logoUrl: string;
  /** Alt text for the logo */
  logoAlt?: string;
  /** Whether to show the "Powered by Finerty" badge (GREY_LABEL tier) */
  showPoweredBy: boolean;
  /** Additional CSS class */
  className?: string;
}

/**
 * LogoWithBranding component for login pages
 *
 * Displays brokerage logo with optional "Powered by Finerty" badge below
 * For GREY_LABEL tier: Shows brokerage logo prominently with smaller "Powered by Finerty" badge right-aligned below
 * For WHITE_LABEL tier: Shows only brokerage logo
 * For NONE tier: Shows Finerty logo prominently
 *
 * Layout specifications:
 * - Brokerage logo: Dominant element, sized to capture attention
 * - "Powered by Finerty": 25-35% height of brokerage logo, right-aligned below with padding
 * - Clear visual separation between elements
 */
export const LogoWithBranding: React.FC<LogoWithBrandingProps> = ({
  logoUrl,
  logoAlt = 'Platform Logo',
  showPoweredBy,
  className = ''
}) => {
  // Check if this is the default platform logo (NONE tier)
  const isDefaultLogo = logoUrl === '/logo.svg' || logoUrl.includes('/logo.svg');

  return (
    <div className={`logo-with-branding ${className}`} style={{ marginBottom: '40px', width: '100%' }}>
      {/* Logo Display */}
      <div style={{ marginBottom: showPoweredBy ? '16px' : '0' }}>
        {isDefaultLogo ? (
          // Display Finerty logo prominently for NONE tier
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '75%',
            maxWidth: '350px'
          }}>
            <FinertyLogo height={48} />
          </div>
        ) : (
          // Display brokerage logo for GREY_LABEL or WHITE_LABEL
          <img
            src={logoUrl}
            alt={logoAlt}
            style={{
              height: '48px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        )}
      </div>

      {/* Powered by Finerty badge - Sub-branding element, right-aligned */}
      {showPoweredBy && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '8px'
        }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #e0e0e0',
              fontSize: '0.7rem',
              color: '#666',
              fontWeight: 500,
              height: '20px'
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>Powered by</span>
            <FinertyLogo height={14} />
          </div>
        </div>
      )}
    </div>
  );
};

export default LogoWithBranding;
